import type { ReactNode } from "react";
import { IconCheck } from "@tabler/icons-react";
import { Logo } from "@/components/ui/logo";
import { cn } from "@/lib/utils/cn";

export type OnboardingStep = 1 | 2 | 3;

const STEPS: { n: OnboardingStep; label: string }[] = [
  { n: 1, label: "Dados da imobiliária" },
  { n: 2, label: "Termos" },
  { n: 3, label: "Aprovação" },
];

function Stepper({ active }: { active: OnboardingStep }) {
  return (
    <ol className="mb-7 flex items-center gap-2 text-[12px] font-medium">
      {STEPS.map((step, i) => {
        const done = step.n < active;
        const current = step.n === active;
        return (
          <li key={step.n} className="flex items-center gap-2">
            <span
              className={cn(
                "grid h-6 w-6 place-items-center rounded-full text-[11px] font-bold transition-colors",
                done && "bg-brand-600 text-white",
                current && "bg-brand-grad text-white shadow-soft",
                !done && !current && "bg-white text-ink-soft ring-1 ring-border",
              )}
            >
              {done ? <IconCheck size={13} /> : step.n}
            </span>
            <span className={cn("hidden sm:inline", current ? "text-ink" : "text-ink-soft")}>
              {step.label}
            </span>
            {i < STEPS.length - 1 && <span className="mx-1 h-px w-6 bg-border" />}
          </li>
        );
      })}
    </ol>
  );
}

export function OnboardingShell({
  children,
  activeStep,
}: {
  children: ReactNode;
  activeStep?: OnboardingStep;
}) {
  return (
    // zoom 0.85 alinha a densidade do onboarding ao app (AppLayout usa o mesmo);
    // minHeight compensa o zoom pra preencher a viewport inteira.
    <div
      className="relative overflow-hidden bg-bg"
      style={{ zoom: 0.85, minHeight: "calc(100vh / 0.85)" }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 -top-32 h-72 w-72 rounded-full bg-brand-400/15 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -left-32 h-72 w-72 rounded-full bg-info/10 blur-3xl"
      />
      <div
        className="relative mx-auto flex flex-col items-center justify-center px-4 py-10"
        style={{ minHeight: "calc(100vh / 0.85)" }}
      >
        <div className="mb-6">
          <Logo />
        </div>
        {activeStep && <Stepper active={activeStep} />}
        <div className="w-full max-w-lg">{children}</div>
      </div>
    </div>
  );
}
